import {
  BarChart3,
  CalendarDays,
  ChevronDown,
  CircleHelp,
  LogOut,
  Settings as SettingsIcon,
  UserRound,
  Users,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'

import TermsGate from '@/features/settings/TermsGate'

import { useBranding } from '@/hooks/useBranding'
import { useBootstrap } from '@/hooks/useBootstrap'
import { LANGUAGES, setLanguage } from '@/i18n'
import { cn, setActiveCurrency } from '@/lib/utils'
import { useAuth } from '@/store/auth'
import OverdueBanner from '@/features/billing/OverdueBanner'
import { OnboardingButton } from '@/features/onboarding/OnboardingButton'
import { PlanUsageBanner } from '@/features/billing/PlanUsage'

interface NavChild {
  to: string
  label: string
}

interface NavSection {
  /** Where the section title itself navigates to. */
  to: string
  label: string
  icon: typeof CalendarDays
  /** Every route that should light the section up, `to` included. */
  match: string[]
  /** Routes that `match` would otherwise swallow but belong to another
   *  section - e.g. /settings/rewards lives under "Inštruktori". */
  exclude?: string[]
  children?: NavChild[]
}

/** Bundled in frontend/public - served at the site root in dev and prod. */
const DEFAULT_LOGO = '/images/learn2ski-logo.png'
/** Na úzkej obrazovke len ikona loga - celý nápis by bol v hlavičke drobný. */
const DEFAULT_MARK = '/images/learn2ski-mark.svg'

export default function AppLayout() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const user = useAuth((s) => s.user)
  const logout = useAuth((s) => s.logout)
  const { data } = useBootstrap()

  // Ikona a názov v záložke prehliadača podľa školy.
  useBranding()

  const [logoBroken, setLogoBroken] = useState(false)
  const usesDefaultLogo =
    logoBroken || !data?.settings.logo_url || data.settings.logo_url === DEFAULT_LOGO

  const cashReceiptsEnabled = data?.settings.module_cash_receipt ?? false
  const pricingEnabled = data?.settings.module_pricing ?? true
  const isAdmin = Boolean(user?.can_administrate)

  // Every formatMoney() call downstream reads this.
  useEffect(() => {
    setActiveCurrency(data?.settings.currency_key)
  }, [data?.settings.currency_key])

  /** The legacy four-section menu (app/templates/@layout.latte). */
  const nav: NavSection[] = [
    {
      to: '/calendar',
      label: t('nav.calendar'),
      icon: CalendarDays,
      match: ['/calendar'],
    },
    {
      to: '/instructors',
      label: t('nav.instructors'),
      icon: UserRound,
      match: ['/instructors', '/rewards', '/settings/rewards'],
      children: [
        { to: '/instructors', label: t('nav.instructorList') },
        { to: '/rewards', label: t('nav.rewards') },
        // Not `settings.rewards` - inside the Nastavenia tab strip "Odmeny" is
        // unambiguous, but here it would collide with the payout list above.
        ...(isAdmin ? [{ to: '/settings/rewards', label: t('nav.rewardSettings') }] : []),
      ],
    },
    {
      to: '/clients',
      label: t('nav.clients'),
      icon: Users,
      match: ['/clients', '/reservations', '/payments', '/cash-receipts'],
      children: [
        { to: '/clients', label: t('nav.clientList') },
        // Zoznam lekcií patrí ku klientom, nie ku kalendáru: odpovedá na
        // otázky o histórii a platbách, nie na to, čo je v stredu o desiatej.
        { to: '/reservations', label: t('nav.reservations') },
        // Bez cenníka niet čo platiť - rovnaká podmienka ako pri "Cenník"
        // nižšie.
        ...(pricingEnabled ? [{ to: '/payments', label: t('nav.payments') }] : []),
        ...(cashReceiptsEnabled
          ? [{ to: '/cash-receipts', label: t('nav.cashReceipts') }]
          : []),
      ],
    },
    {
      // Prehľady o chode systému, nie o ľuďoch: čo sa udialo (štatistiky),
      // kto to zmenil (história) a čo z neho odišlo (pošta). Vlastná sekcia
      // preto, že pri klientoch to bolo vedľa zoznamov ľudí a v nastaveniach
      // vedľa vecí, ktoré sa nastavujú - ani jedno nie je, čo tieto tri sú.
      // Štatistiky (tržby, výkon inštruktorov) len vlastník a administrátor
      // (VOP 1.1) - ostatní začínajú históriou zmien.
      to: isAdmin ? '/statistics' : '/changelog',
      label: t('nav.reports'),
      icon: BarChart3,
      match: ['/statistics', '/changelog', '/emails'],
      children: [
        ...(isAdmin ? [{ to: '/statistics', label: t('nav.statistics') }] : []),
        { to: '/changelog', label: t('nav.changelog') },
        // Fronta nesie celé telá správ aj adresy klientov - je to citlivejšie
        // než zoznam rezervácií, preto len pre administrátora.
        ...(isAdmin ? [{ to: '/emails', label: t('nav.emails') }] : []),
      ],
    },
    {
      to: '/settings',
      label: t('nav.settings'),
      icon: SettingsIcon,
      match: ['/settings'],
      exclude: ['/settings/rewards'],
      children: [
        { to: '/settings/account', label: t('settings.myAccount') },
        ...(isAdmin
          ? [
              { to: '/settings/users', label: t('settings.users') },
              { to: '/settings/system', label: t('settings.system') },
              ...(pricingEnabled
                ? [{ to: '/settings/pricing', label: t('settings.pricing') }]
                : []),
              { to: '/settings/integrations', label: t('settings.integrations') },
            ]
          : []),
      ],
    },
  ]

  // Rola „prezerajúci“ vidí len kalendár - a v nastaveniach svoj účet
  // (heslo, MFA). Server jej iné API aj tak odmietne (`viewer_allowed`).
  const isViewer = user?.role === 'viewer'
  const visibleNav = isViewer
    ? nav.filter((section) => section.to === '/calendar' || section.to === '/settings')
    : nav
  const location = useLocation()
  const viewerPathAllowed =
    location.pathname.startsWith('/calendar') ||
    location.pathname === '/settings' ||
    location.pathname === '/settings/account' ||
    location.pathname.startsWith('/help')

  async function handleLogout() {
    await logout()
    navigate('/login', { replace: true })
  }

  if (isViewer && !viewerPathAllowed) return <Navigate to="/calendar" replace />

  return (
    <div className="flex min-h-screen flex-col">
      {/* The legacy bar: brown (#4e4242), white uppercase labels, a 3px red
          rule under the active one, and the logo sitting on white. */}
      <header className="sticky top-0 z-40 bg-bark">
        {/* 51px = the old h-14 (56px) less 5. Arbitrary rather than a scale
            step because h-12 (48) would drop 8 and h-13 does not exist. */}
        <div className="flex h-[51px] items-stretch gap-0">
          <div className="flex min-w-0 shrink-0 items-center gap-2 bg-white px-2 sm:px-3">
            {/* Falls back to the bundled Learn2Ski logo when the school has
                not uploaded one of its own. */}
            {usesDefaultLogo ? (
              <img src={DEFAULT_MARK} alt="Learn2Ski.me" className="h-8 w-8 sm:hidden" />
            ) : null}
            <img
              src={usesDefaultLogo ? DEFAULT_LOGO : data?.settings.logo_url}
              alt={data?.settings.display_company_name ?? 'Learn2Ski'}
              className={cn(
                'h-8 w-auto max-w-[4.5rem] object-contain sm:h-9 sm:max-w-[9rem]',
                usesDefaultLogo && 'hidden sm:block',
              )}
              // Logo školy, ktoré sa nedá načítať (napr. odkaz zo starého
              // systému), nahradí predvolené Learn2Ski.me.
              onError={() => setLogoBroken(true)}
            />
            <span className="hidden truncate text-[15px] font-bold uppercase text-brand-600 xl:inline">
              {data?.settings.display_system_name ?? 'Learn2Ski'}
            </span>
          </div>

          <nav className="flex min-w-0 flex-1 items-stretch">
            {visibleNav.map((section, index) => (
              // Podmenu posledných dvoch položiek sa otvára doľava - na mobile
              // by inak vyšlo za pravý okraj obrazovky.
              <NavSectionItem key={section.to} section={section} alignRight={index >= nav.length - 2} />
            ))}
          </nav>

          <div className="flex shrink-0 items-center gap-2 px-2 sm:gap-3 sm:px-3">
            <select
              value={i18n.language}
              onChange={(e) => setLanguage(e.target.value)}
              className="rounded border border-white/25 bg-transparent px-1 py-1 text-xs uppercase text-white sm:px-1.5 [&>option]:text-ink"
              aria-label="Jazyk"
            >
              {LANGUAGES.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>

            <div className="hidden text-right text-xs leading-tight text-white sm:block">
              <div className="font-medium">{user?.full_name || user?.email}</div>
              <div className="text-white/60">
                {t(`settings.roles.${user?.role ?? 'manager'}`)}
              </div>
            </div>

            <OnboardingButton className="p-1.5 text-white hover:text-brand-300" />

            <NavLink
              to="/help"
              className="p-1.5 text-white hover:text-brand-300"
              title={t('help.open')}
              aria-label={t('help.open')}
            >
              <CircleHelp size={18} />
            </NavLink>

            <button
              type="button"
              onClick={handleLogout}
              className="p-1.5 text-white hover:text-brand-300"
              title={t('nav.logout')}
              aria-label={t('nav.logout')}
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      {data?.sandbox?.enabled ? (
        // Testovacie prostredie pred migráciou zo starého systému - stále na
        // očiach, aby nikto nepovažoval zápisy za ostré.
        <div className="bg-amber-400 px-4 py-1.5 text-center text-sm font-medium text-amber-950">
          {t('sandbox.banner', { email: data.sandbox.email || '—' })}
        </div>
      ) : null}
      <OverdueBanner isAdmin={isAdmin} />
      <PlanUsageBanner isAdmin={isAdmin} />

      <main className="flex-1 p-4">
        <Outlet />
      </main>

      {/* `/vop` servíruje Django, nie SPA - preto obyčajný odkaz, nie `Link`. */}
      <footer className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-slate-200 px-4 py-1.5 text-[11px] text-slate-400">
        <span>© {new Date().getFullYear()} Learn2Ski.me</span>
        <a
          href="/vop"
          target="_blank"
          rel="noopener"
          className="hover:text-slate-600 hover:underline"
        >
          {t('footer.terms')}
        </a>
      </footer>

      {/* Nové znenie obchodných podmienok. Nie je to brána — okno sa dá
          zavrieť a pracovať ďalej (viď `TermsGate`); patrí sem preto, že
          správca sa má o novej verzii dozvedieť, nech je v aplikácii
          kdekoľvek. */}
      <TermsGate />
    </div>
  )
}

/**
 * One top-level menu entry. The title is always a link (clicking "Inštruktori"
 * goes to the list, exactly like the legacy `navbar-brand`-style anchor); the
 * caret opens the section. The panel also opens on hover, which is what the
 * Bootstrap menu it replaces did.
 */
/** Úzka obrazovka - položka s podmenu sa tam ťuknutím otvára, nie preklikne
 *  (na samostatnú šípku nie je v hlavičke miesto). Zhoduje sa s `sm:`. */
const isNarrow = () => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches

function NavSectionItem({ section, alignRight = false }: { section: NavSection; alignRight?: boolean }) {
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const closeTimer = useRef<number | null>(null)

  useEffect(
    () => () => {
      if (closeTimer.current) window.clearTimeout(closeTimer.current)
    },
    [],
  )

  // Close as soon as the route changes - otherwise the panel lingers over the
  // page the user just navigated to.
  useEffect(() => setOpen(false), [location.pathname])

  const covers = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`)
  const active = section.match.some(covers) && !(section.exclude ?? []).some(covers)
  const Icon = section.icon

  function scheduleClose() {
    if (closeTimer.current) window.clearTimeout(closeTimer.current)
    closeTimer.current = window.setTimeout(() => setOpen(false), 150)
  }

  function cancelClose() {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }

  return (
    <div
      className="group/nav relative flex items-stretch"
      onMouseEnter={() => {
        cancelClose()
        // Na dotykovej obrazovke prehliadač pred ťuknutím vyšle aj „najetie
        // myšou“ - podmenu by sa otvorilo a ťuknutie by ho hneď zavrelo.
        if (section.children && !isNarrow()) setOpen(true)
      }}
      onMouseLeave={scheduleClose}
    >
      <NavLink
        to={section.to}
        onClick={(e) => {
          if (section.children && isNarrow()) {
            e.preventDefault()
            setOpen((v) => !v)
          }
        }}
        className={cn(
          'relative flex shrink-0 items-center gap-1.5 px-2.5 text-[13px] sm:px-4',
          'font-medium uppercase tracking-wide text-white transition-colors',
        )}
      >
        <Icon size={16} className="lg:hidden" />
        <span className="hidden lg:inline">{section.label}</span>

        {/* The rule hangs *below* the brown bar rather than sitting on its
            edge, so it reads against the white page. `bottom: -8px` with a 3px
            bar leaves the 5px gap. Absolutely positioned rather than a border
            because a border cannot escape the element's box. */}
        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-x-3 -bottom-2 h-[3px] transition-colors',
            active ? 'bg-brand-600' : 'bg-transparent group-hover/nav:bg-brand-600/50',
          )}
        />
        {section.children ? (
          <ChevronDown
            size={13}
            className={cn('hidden transition-transform sm:block', open && 'rotate-180')}
            onClick={(e) => {
              // Tapping the caret opens the panel instead of navigating.
              e.preventDefault()
              e.stopPropagation()
              setOpen((v) => !v)
            }}
          />
        ) : null}
      </NavLink>

      {section.children && open ? (
        <ul
          // `mt-2` clears the rule hanging below the bar; without it the white
          // panel would cover the active item's own indicator.
          className={cn(
            'absolute top-full z-50 mt-2 min-w-[13rem] border border-slate-300 bg-white py-1 shadow-lg',
            alignRight ? 'right-0' : 'left-0',
          )}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          {section.children.map((child) => (
            <li key={child.to}>
              <NavLink
                to={child.to}
                end
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'block px-4 py-[5px] text-[13px] hover:bg-slate-100',
                    isActive ? 'font-semibold text-brand-600' : 'text-ink',
                  )
                }
              >
                {child.label}
              </NavLink>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
